import { motion } from "framer-motion";
import { Wallet, CheckCircle2, Undo2 } from "lucide-react";
import { FitKeyPassBack } from "@/components/fittin/FitKeyPass";

const STEPS = [
  {
    icon: Wallet,
    title: "Add your FitKey to Wallet",
    body: "Create it once from your best-fitting pair. It sits next to your cards and tickets — no app, no account.",
  },
  {
    icon: CheckCircle2,
    title: "Confirm the fit from the pass",
    body: "After a purchase, open the pass and tap too small, perfect or too large. Every answer sharpens your next size.",
  },
  {
    icon: Undo2,
    title: "Doesn't fit? Return in one tap",
    body: "Request the return from the same pass. The FitKey identifies you, so there's nothing to log into.",
  },
];

// RETURNS — product pillar. The loop runs from the Wallet pass, not an account area.
export default function ReturnsPillar() {
  return (
    <section
      id="returns"
      data-testid="returns-section"
      className="relative py-24 md:py-32 bg-[#F2F2F2] border-y border-black/5 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
            <span className="h-px w-8 bg-black/40" />
            Returns, built in
          </div>
          <h2
            data-testid="returns-headline"
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-[-0.035em] font-medium leading-[1.02]"
          >
            Confirm the fit. <span className="text-neutral-400">Or send it back. From your Wallet.</span>
          </h2>
          <p className="mt-5 max-w-xl text-[15.5px] text-neutral-600 leading-relaxed">
            Size advice shouldn’t end at checkout. Your FitKey closes the loop: what you keep and
            what you return teaches it your true size, brand by brand.
          </p>

          <ol className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {STEPS.map((s, i) => (
              <motion.li
                key={s.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`returns-step-${i + 1}`}
                className="glass-card rounded-2xl p-5"
              >
                <div className="flex items-center justify-between">
                  <div className="h-9 w-9 rounded-xl border border-black/10 bg-white grid place-items-center">
                    <s.icon className="h-4 w-4" strokeWidth={1.6} />
                  </div>
                  <span className="font-mono text-[11px] text-neutral-400">0{i + 1}</span>
                </div>
                <div className="mt-5 text-[15.5px] tracking-tight font-medium leading-snug">{s.title}</div>
                <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed">{s.body}</p>
              </motion.li>
            ))}
          </ol>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <div className="relative w-full max-w-[340px] mx-auto">
            <div
              aria-hidden
              className="absolute inset-0 -translate-x-5 translate-y-3 -rotate-[6deg] rounded-[28px] glass-card"
            />
            <FitKeyPassBack
              brand="Adidas"
              model="NMD R1"
              size={{ unit: "EU", value: "42" }}
              testid="returns-mockup"
              className="relative"
            />
            <div className="mt-5 flex items-center justify-center gap-2 text-[11.5px] text-neutral-500">
              <span
                data-testid="returns-mockup-chip"
                className="rounded-full border border-black/10 bg-white px-2 py-0.5 text-[10px] uppercase tracking-[0.18em]"
              >
                Mockup
              </span>
              Back of the pass. Each action opens a tap-through page.
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
